"use client";
import React from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Lock,
  Server,
  Eye,
  AlertTriangle,
  Mail,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12 },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  },
};

const principles = [
  {
    icon: Shield,
    titleKey: "security.principle_1_title",
    descKey: "security.principle_1_desc",
  },
  {
    icon: Lock,
    titleKey: "security.principle_2_title",
    descKey: "security.principle_2_desc",
  },
  {
    icon: Server,
    titleKey: "security.principle_3_title",
    descKey: "security.principle_3_desc",
  },
  {
    icon: Eye,
    titleKey: "security.principle_4_title",
    descKey: "security.principle_4_desc",
  },
];

export const Security = () => {
  const { t } = useTranslation("translation");

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-blue-900 via-gray-50 to-white">
      {/* Ambient background */}
      <div className="absolute -top-40 -left-40 w-[32rem] h-[32rem] bg-blue-200/30 blur-3xl rounded-full" />
      <div className="absolute top-1/3 -right-40 w-[28rem] h-[28rem] bg-indigo-200/30 blur-3xl rounded-full" />

      {/* HERO */}
      <motion.div
        className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16
                   pt-28 sm:pt-32 pb-24 text-center"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div
          variants={item}
          className="inline-flex items-center gap-2 px-4 py-1.5 mb-6
                     rounded-full bg-blue-100 text-blue-800
                     text-xs font-semibold tracking-widest uppercase"
        >
          <Shield size={14} />
          {t("security.badge")}
        </motion.div>

        <motion.h1
          variants={item}
          className="text-4xl sm:text-5xl lg:text-6xl
                     font-extrabold text-slate-900 tracking-tight mb-6"
        >
          {t("security.title")}
        </motion.h1>

        <motion.p
          variants={item}
          className="max-w-3xl mx-auto text-lg sm:text-xl
                     text-slate-600 leading-relaxed"
        >
          {t("security.subtitle")}
        </motion.p>
      </motion.div>

      {/* PRINCIPLES */}
      <motion.div
        className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pb-24"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {principles.map(({ icon: Icon, titleKey, descKey }) => (
            <motion.div
              key={titleKey}
              variants={item}
              className="group relative rounded-3xl bg-white p-8
                         shadow-md hover:shadow-2xl
                         hover:-translate-y-1
                         transition-all duration-300"
            >
              {/* Glow */}
              <div
                className="absolute -inset-6 rounded-3xl bg-blue-400/20
                           blur-2xl opacity-0 group-hover:opacity-100
                           transition-opacity duration-300"
              />

              <div className="relative z-10">
                <div
                  className="flex items-center justify-center
                             w-14 h-14 rounded-2xl mb-6
                             bg-gradient-to-br from-blue-600 to-indigo-600
                             text-white shadow-lg"
                >
                  <Icon size={26} />
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mb-3">
                  {t(titleKey)}
                </h3>

                <p className="text-sm text-slate-600 leading-relaxed">
                  {t(descKey)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* RESPONSIBLE DISCLOSURE */}
      <motion.div
        className="relative max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 pb-32"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <motion.div
          variants={item}
          className="rounded-3xl bg-slate-900 text-white
                     p-10 sm:p-14 shadow-2xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle />
            <h2 className="text-2xl font-semibold">
              {t("security.disclosure_title")}
            </h2>
          </div>

          <p className="text-slate-300 leading-relaxed max-w-3xl mb-8">
            {t("security.disclosure_desc")}
          </p>

          <a
            href="mailto:info@lakche.com"
            className="inline-flex items-center gap-2
                       px-6 py-3 rounded-full
                       bg-white text-slate-900
                       font-semibold text-sm
                       hover:bg-slate-100 transition"
          >
            <Mail size={16} />
            info@lakche.com
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};
